// v4 ticket T4: notes on a version. The composer (a note at the playhead: change / like / question / whole video,
// From here / To here, drawing on the paused frame, reference pictures by paste, drop or upload), the list (what
// still needs something, whole-video notes, closed ones; it follows the playhead) and one note's detail: its moment
// and context line, status, the plan with prices and options, Approve (a priced fix asks the quoted yes first),
// Make the fix, the free preview with Before / After, Skip, the thread and a reply. A11: edits send `updated_at` and
// a stale one re-reads (patchFresh).
import { useEffect, useRef, useState, type ReactNode } from "react";
import { ApiError } from "../../api";
import { director } from "../../director";
import { setFold } from "../../fold";
import { fmtTime } from "../../timeline";
import { toast } from "../../store";
import {
  approveLabel,
  beforeAfter,
  credits,
  groupNotes,
  NOTE_TYPES,
  noteActions,
  noteContextLine,
  notesApi,
  noteTime,
  onPreviewEvent,
  PART_LABEL,
  patchFresh,
  priceLabel,
  statusChip,
  yesLine,
  type Mark,
  type MarkType,
  type Note,
  type NotePart,
  type NoteType,
  type Preview,
} from "../../notes";
import type { Asset } from "../../types";
import { Icon } from "../bits";
import { Fold } from "../Fold";
import { dragHasFiles, UploadSheet } from "../Upload";

// ------------------------------------------------------------------ composer

export type Draft = {
  type: NoteType;
  start: number;
  end: number | null;
  body: string;
  marks: Mark[];
  /** the tool while drawing on the paused frame */
  tool: MarkType | null;
  refs: Asset[];
  parts?: NotePart[];
  /** "the Dough fold sound": a note started from a lane */
  on?: string;
};
export const newDraft = (t: number, o: Partial<Draft> = {}): Draft => ({ type: "change", start: t, end: null, body: "", marks: [], tool: null, refs: [], ...o });

const PLACEHOLDER: Record<NoteType, string> = {
  change: "What should change here? (\"her line sounds rushed\")",
  like: "What do you like here? (optional)",
  question: "What do you want to know?",
  general: "A note on the whole video",
};

export function Composer({ draft, setDraft, t, projectId, saving, onSave, onCancel }: { draft: Draft; setDraft: (f: (d: Draft) => Draft) => void; t: number; projectId: string; saving: boolean; onSave: () => void; onCancel: () => void }) {
  const [upload, setUpload] = useState<File[] | null>(null);
  const [over, setOver] = useState(false);
  const general = draft.type === "general";
  const canSave = !saving && (draft.type === "like" || !!draft.body.trim() || draft.marks.length > 0);
  const files = (list: FileList | null | undefined) => {
    const pics = Array.from(list ?? []).filter((f) => f.type.startsWith("image/"));
    if (pics.length) setUpload(pics.slice(0, 6 - draft.refs.length));
  };
  return (
    <section
      className={`rv-compose${over ? " is-over" : ""}`}
      aria-label="New note"
      data-unsaved={draft.body || draft.marks.length ? "true" : undefined}
      onDragOver={(e) => {
        if (!dragHasFiles(e)) return;
        e.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setOver(false);
        files(e.dataTransfer.files);
      }}
    >
      <div className="rv-types" role="radiogroup" aria-label="Kind of note">
        {NOTE_TYPES.map((x) => (
          <button key={x.type} type="button" role="radio" aria-checked={draft.type === x.type} className={`rv-type${draft.type === x.type ? " is-on" : ""}`} onClick={() => setDraft((d) => ({ ...d, type: x.type, tool: x.type === "general" ? null : d.tool }))} title={x.hint}>
            {x.label}
          </button>
        ))}
      </div>
      {draft.on && <p className="rv-on">On {draft.on}</p>}
      {!general && (
        <div className="rv-range">
          <button type="button" className="rv-range-btn" onClick={() => setDraft((d) => ({ ...d, start: t, end: d.end !== null && d.end > t + 0.05 ? d.end : null }))} aria-label={`From ${fmtTime(draft.start)}. Tap to move it here`}>
            <small>From</small> {fmtTime(draft.start)}
          </button>
          <span className="rv-range-dash" aria-hidden>
            –
          </span>
          <button type="button" className={`rv-range-btn${draft.end === null ? " is-empty" : ""}`} onClick={() => setDraft((d) => (t > d.start + 0.05 ? { ...d, end: t } : { ...d, end: null }))} aria-label={draft.end === null ? "To here (sets the end at the playhead)" : `To ${fmtTime(draft.end)}. Tap to move it here`}>
            <small>To</small> {draft.end === null ? "here" : fmtTime(draft.end)}
          </button>
          {draft.end !== null && (
            <button type="button" className="icon-btn" aria-label="Just this moment" title="Just this moment" onClick={() => setDraft((d) => ({ ...d, end: null }))}>
              <Icon name="close" size={16} />
            </button>
          )}
        </div>
      )}
      {!general && (
        <div className="rv-tools" role="group" aria-label="Draw on the picture">
          {(["box", "arrow", "pen"] as const).map((k) => (
            <button key={k} type="button" className={`rv-tool${draft.tool === k ? " is-on" : ""}`} aria-pressed={draft.tool === k} onClick={() => setDraft((d) => ({ ...d, tool: d.tool === k ? null : k }))}>
              <ToolIcon k={k} /> {k === "box" ? "Box" : k === "arrow" ? "Arrow" : "Pen"}
            </button>
          ))}
          {draft.marks.length > 0 && (
            <>
              <button type="button" className="rv-tool" onClick={() => setDraft((d) => ({ ...d, marks: d.marks.slice(0, -1) }))}>
                <Icon name="restore" size={14} /> Undo
              </button>
              <button type="button" className="rv-tool" onClick={() => setDraft((d) => ({ ...d, marks: [] }))}>
                Clear
              </button>
            </>
          )}
        </div>
      )}
      {draft.tool && <p className="rv-hint">Draw on the picture. Shapes say what; your words say why.</p>}
      <textarea
        className="rv-text"
        rows={3}
        value={draft.body}
        placeholder={PLACEHOLDER[draft.type]}
        aria-label="The note"
        data-autofocus
        onChange={(e) => {
          const body = e.target.value;
          setDraft((d) => ({ ...d, body }));
        }}
        onPaste={(e) => {
          if (e.clipboardData.files.length) {
            e.preventDefault();
            files(e.clipboardData.files);
          }
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" && (e.metaKey || e.ctrlKey) && canSave) onSave();
        }}
      />
      <div className="rv-refs">
        {draft.refs.map((a) => (
          <span key={a.id} className="rv-ref">
            {a.thumbUrl || a.url ? <img src={a.thumbUrl ?? a.url ?? ""} alt={a.name} /> : <Icon name="image" />}
            <button type="button" aria-label={`Remove ${a.name}`} onClick={() => setDraft((d) => ({ ...d, refs: d.refs.filter((x) => x.id !== a.id) }))}>
              <Icon name="close" size={12} />
            </button>
          </span>
        ))}
        {draft.refs.length < 6 && (
          <button type="button" className="rv-ref-add" onClick={() => setUpload([])}>
            <Icon name="image" size={16} /> Add a picture
          </button>
        )}
        <span className="rv-ref-hint">or paste or drop one here</span>
      </div>
      <div className="rv-compose-acts">
        <button type="button" className="btn btn-quiet" onClick={onCancel}>
          Cancel
        </button>
        <button data-action="review.add-note" type="button" className="btn btn-primary" disabled={!canSave} onClick={onSave}>
          {saving ? "Saving…" : draft.type === "question" ? "Ask" : "Add note"}
        </button>
      </div>
      {upload && (
        <UploadSheet
          scope={{ level: "project", id: projectId }}
          scopeName="this video"
          preset="picture"
          files={upload}
          onClose={() => setUpload(null)}
          onDone={(items) => {
            setDraft((d) => ({ ...d, refs: [...d.refs, ...items.filter((x) => !d.refs.some((r) => r.id === x.id))].slice(0, 6) }));
            setUpload(null);
          }}
        />
      )}
    </section>
  );
}

function ToolIcon({ k }: { k: MarkType }) {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
      {k === "box" && <rect x="2" y="3" width="10" height="8" rx="1.5" />}
      {k === "arrow" && <path d="M2.5 11.5 11 3M11 3H6.5M11 3v4.5" />}
      {k === "pen" && <path d="M2 10c2-3 3 1 5-2s3-1 5-4" />}
    </svg>
  );
}

// ------------------------------------------------------------------ the list

/** the done-and-kept notes sit in one Fold (spec-v4 5.2), remembered like every other fold */
const CLOSED_FOLD = "review.closed";

export function NotesList({ notes, selected, near, onSelect, detail }: { notes: Note[]; selected: string | null; near: string | null; onSelect: (n: Note) => void; detail?: (n: Note) => ReactNode }) {
  const g = groupNotes(notes);
  // a done note picked from a pin or a link opens the fold it sits in
  const closedPicked = !!selected && g.closed.some((n) => n.id === selected);
  useEffect(() => {
    if (closedPicked) setFold(CLOSED_FOLD, true);
  }, [closedPicked]);
  const row = (n: Note) => <NoteRow key={n.id} n={n} on={n.id === selected} near={n.id === near} onSelect={onSelect} detail={n.id === selected ? detail?.(n) : null} />;
  if (!notes.length)
    return (
      <div className="rv-empty">
        <p>No notes yet.</p>
        <p className="muted">Pause where something should change and tap Add a note. You can draw on the picture too.</p>
      </div>
    );
  return (
    <div className="rv-list">
      {g.live.length > 0 && <ol className="rv-notes">{g.live.map(row)}</ol>}
      {g.general.length > 0 && (
        <>
          <h3 className="rv-sub">The whole video</h3>
          <ol className="rv-notes">{g.general.map(row)}</ol>
        </>
      )}
      {g.closed.length > 0 && (
        <Fold id={CLOSED_FOLD} title="Done and kept" count={g.closed.length} className="rv-closed">
          <ol className="rv-notes is-closed">{g.closed.map(row)}</ol>
        </Fold>
      )}
    </div>
  );
}

function NoteRow({ n, on, near, onSelect, detail }: { n: Note; on: boolean; near: boolean; onSelect: (n: Note) => void; detail: ReactNode }) {
  const chip = statusChip(n);
  const ref = useRef<HTMLLIElement>(null);
  // A6: the list follows the playhead
  useEffect(() => {
    if (near && !on) intoPanel(ref.current, true);
  }, [near, on]);
  // a note picked from a pin or a link comes into view
  useEffect(() => {
    if (on) intoPanel(ref.current, false);
  }, [on]);
  return (
    <li ref={ref} className={`rv-note${on ? " is-on" : ""}${near ? " is-near" : ""}`} data-note={n.id}>
      <button type="button" className="rv-note-head" aria-expanded={on} onClick={() => onSelect(n)}>
        <span className="rv-note-time">{noteTime(n)}</span>
        <span className={`rv-chip tone-${chip.tone}`}>{chip.label}</span>
        {n.kind !== "fix" && <span className="rv-kind">{n.kind === "like" ? "Like" : "Question"}</span>}
        {n.context.from && (
          <span className="rv-kind rv-viewer" title={n.context.from.name ? `${n.context.from.name}, from a share link` : "From a share link"}>
            From a viewer
          </span>
        )}
        <span className="rv-note-ctx">{noteContextLine(n)}</span>
        <span className="rv-note-body">{n.body || (n.kind === "like" ? "Keep this as it is." : n.marks.length ? "Marked on the picture." : "")}</span>
        {(n.marks.length > 0 || n.refs.length > 0) && (
          <span className="rv-note-extras" aria-label={`${n.marks.length} marks, ${n.refs.length} pictures`}>
            {n.marks.length > 0 && <ToolIcon k="box" />}
            {n.refs.length > 0 && <Icon name="image" size={14} />}
          </span>
        )}
      </button>
      {detail}
    </li>
  );
}

// ------------------------------------------------------------------ one note

export type Player = {
  /** play the version from a second and a half before the note */
  moment: (n: Note) => void;
  /** play before, after, or before then after */
  compare: (p: Preview, which: "before" | "after" | "both") => void;
};

type Ask = { kind: "yes"; credits: number; pick: number | null } | { kind: "liked"; text: string } | { kind: "delete" } | null;

export function NoteDetail({ n, onChange, onDelete, player }: { n: Note; onChange: (n: Note) => void; onDelete: (id: string) => void; player: Player }) {
  const [busy, setBusy] = useState<string | null>(null);
  const [ask, setAsk] = useState<Ask>(null);
  const [pick, setPick] = useState<number>(n.pick ?? 0);
  const [reply, setReply] = useState("");
  const [editing, setEditing] = useState<string | null>(null);
  const [preview, setPreview] = useState<Preview | null>(null);
  const a = noteActions(n);
  const options = n.options && n.options.length > 1 ? n.options : null;
  const price = options ? (options[pick]?.credits ?? n.plan?.credits ?? 0) : (n.plan?.credits ?? 0);

  useEffect(() => setPick(n.pick ?? 0), [n.id, n.pick]);

  // the latest preview of this note (a done fix may already have one)
  useEffect(() => {
    let live = true;
    setPreview(null);
    if (!a.preview) return;
    notesApi
      .previews(n.projectId, n.id)
      .then((list) => live && setPreview(list.find((p) => p.status === "done" && p.clip) ?? list[0] ?? null))
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [n.id, a.preview]); // eslint-disable-line react-hooks/exhaustive-deps

  // a running preview's progress (SSE "preview", src/session.ts)
  useEffect(() => {
    if (!preview || preview.status === "done" || preview.status === "failed" || preview.status === "cancelled") return;
    const id = preview.id;
    const off = onPreviewEvent((e) => {
      if (e.previewId !== id) return;
      if (e.status === "done") notesApi.preview(id).then(setPreview).catch(() => undefined);
      else setPreview((p) => (p && p.id === id ? { ...p, status: e.status, progress: e.progress, error: e.error } : p));
    });
    // no events (a dropped stream): ask now and then
    const poll = window.setInterval(() => notesApi.preview(id).then((p) => (p.status !== "queued" && p.status !== "running" ? setPreview(p) : undefined)).catch(() => undefined), 5000);
    return () => {
      off();
      window.clearInterval(poll);
    };
  }, [preview?.id, preview?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  const run = async (label: string, f: () => Promise<void>) => {
    setBusy(label);
    try {
      await f();
    } catch (e) {
      handle(e);
    } finally {
      setBusy(null);
    }
  };
  const handle = (e: unknown) => {
    if (e instanceof ApiError && e.code === "CONFIRM_REQUIRED") {
      const c = typeof (e.details as { credits?: number } | null)?.credits === "number" ? (e.details as { credits: number }).credits : price;
      return setAsk({ kind: "yes", credits: c, pick: options ? pick : null });
    }
    if (e instanceof ApiError && e.code === "LIKED") return setAsk({ kind: "liked", text: e.message });
    toast(`${(e as Error).message}`, "bad");
  };

  const plan = () =>
    run("plan", async () => {
      const r = await notesApi.plan(n.id, options ? { pick } : {});
      onChange(r.note);
      if (r.ask) director.context(`The studio couldn't tell what to change for the note at ${noteTime(r.note)} and asked: "${r.ask}".`);
    });

  /** Approve then make the fix. A priced plan asks the yes first (quoted price); free ones just go. */
  const approve = (confirmed: boolean) =>
    run("approve", async () => {
      if (price > 0 && !confirmed) return setAsk({ kind: "yes", credits: price, pick: options ? pick : null });
      setAsk(null);
      const ok = await notesApi.approve(n.id, { ...(options ? { pick } : {}), ...(price > 0 ? { confirmed: true } : {}) });
      onChange(ok);
      director.context(`The user approved the fix for the note at ${noteTime(ok)}${price ? ` (about ${credits(price)})` : " (free)"} by tapping.`);
      await fix(false);
    });

  const fix = async (override: boolean) => {
    const r = await notesApi.apply(n.id, override ? { override: true } : {});
    onChange(r.note);
    if (r.needsSpend) return setAsk({ kind: "yes", credits: r.needsSpend.credits, pick: null });
    setAsk(null);
    toast(r.status === "done" ? "Fixed. Preview it to hear the change." : "Making the fix. It shows here when it's done.", "good");
  };

  const patch = (label: string, b: Parameters<typeof patchFresh>[2], retry = false, after?: () => void) =>
    run(label, async () => {
      const r = await patchFresh(notesApi, n, b, { retry });
      onChange(r.note);
      if (r.stale) toast("This note changed somewhere else. Here's the latest.", "info");
      else after?.();
    });

  const startPreview = () =>
    run("preview", async () => {
      const p = await notesApi.previewNote(n.id);
      setPreview(p);
    });

  const ba = preview?.status === "done" && preview.clip ? beforeAfter(preview, "") : null;
  const closed = ["done", "skipped", "stale", "wontfix"].includes(n.status);

  return (
    <div className="rv-detail">
      <div className="rv-detail-row">
        {!n.general && (
          <button type="button" className="btn btn-quiet btn-small" onClick={() => player.moment(n)}>
            <Icon name="play" size={14} /> Play this moment
          </button>
        )}
        {n.parts.length > 0 && <span className="rv-parts">{n.parts.map((p) => PART_LABEL[p]).join(" · ")}</span>}
        <span className="rv-acts-gap" />
        {editing === null && (n.status === "open" || n.status === "planned") && (
          <button type="button" className="icon-btn" aria-label="Edit the note" title="Edit" onClick={() => setEditing(n.body)}>
            <Icon name="edit" size={16} />
          </button>
        )}
        {n.status !== "working" && (
          <button type="button" className="icon-btn" aria-label="Delete the note" title="Delete" onClick={() => setAsk({ kind: "delete" })}>
            <Icon name="trash" size={16} />
          </button>
        )}
      </div>

      {editing !== null ? (
        <div className="rv-edit">
          <textarea className="rv-text" rows={3} value={editing} onChange={(e) => setEditing(e.target.value)} aria-label="Edit the note" />
          <div className="rv-compose-acts">
            <button type="button" className="btn btn-quiet btn-small" onClick={() => setEditing(null)}>
              Cancel
            </button>
            <button data-action="review.edit-note" type="button" className="btn btn-primary btn-small" disabled={!editing.trim() || busy === "edit"} onClick={() => patch("edit", { body: editing.trim() }, false, () => setEditing(null))}>
              Save
            </button>
          </div>
        </div>
      ) : null}

      {(n.frame?.url || n.refs.some((r) => r.url)) && (
        <div className="rv-pics">
          {n.frame?.url && <img src={n.frame.url} alt="The frame with your marks" className="rv-pic-frame" />}
          {n.refs.filter((r) => r.url).map((r) => (
            <img key={r.assetId} src={r.url!} alt="A reference picture" className="rv-pic-ref" />
          ))}
        </div>
      )}

      {/* the answer to a question */}
      {n.kind === "question" && n.plan?.answer && <p className="rv-answer">{n.plan.answer}</p>}

      {/* the plan */}
      {n.kind === "fix" && n.plan && !closed && (
        <div className="rv-plan">
          {options ? (
            <div className="rv-options" role="radiogroup" aria-label="Ways to fix it">
              {options.map((o, i) => (
                <button data-action="review.option" key={i} type="button" role="radio" aria-checked={pick === i} className={`rv-option${pick === i ? " is-on" : ""}`} onClick={() => setPick(i)} disabled={n.status === "approved" || n.status === "working"}>
                  <span className="rv-option-head">
                    <b>{String.fromCharCode(65 + i)}</b> {o.label}
                    <span className="rv-price">{priceLabel(o.credits)}</span>
                  </span>
                  <ul>
                    {o.steps.map((s, k) => (
                      <li key={k}>{stepText(s.label)}</li>
                    ))}
                  </ul>
                </button>
              ))}
            </div>
          ) : (
            <>
              <p className="rv-plan-head">
                The plan <span className="rv-price">{priceLabel(n.plan.credits)}</span>
              </p>
              <ul className="rv-steps">
                {n.plan.steps.map((s, k) => (
                  <li key={k}>
                    {stepText(s.label)}
                    <span className={`rv-step-price${s.free ? " is-free" : ""}`}>{s.free ? "Free" : credits(s.credits)}</span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}
      {n.kind === "fix" && n.status === "done" && n.plan && <p className="rv-done">Done: {n.plan.summary || n.plan.steps.map((s) => stepText(s.label)).join("; ")}</p>}
      {n.status === "working" && <p className="rv-working">Making the fix…</p>}
      {n.blockedReason && n.status !== "open" && <p className="rv-blocked">{n.blockedReason}</p>}

      {/* the quoted yes / the like lock / delete */}
      {ask?.kind === "yes" && (
        <div className="rv-ask" role="alertdialog" aria-label="Spend credits?">
          <p>{yesLine(ask.credits)}</p>
          <div className="rv-compose-acts">
            <button type="button" className="btn btn-quiet btn-small" onClick={() => setAsk(null)}>
              Not now
            </button>
            <button data-action="review.apply" type="button" className="btn btn-primary btn-small" disabled={!!busy} onClick={() => approve(true)} data-autofocus>
              Yes, spend {credits(ask.credits)}
            </button>
          </div>
        </div>
      )}
      {ask?.kind === "liked" && (
        <div className="rv-ask" role="alertdialog" aria-label="You liked this part">
          <p>{ask.text}</p>
          <div className="rv-compose-acts">
            <button type="button" className="btn btn-quiet btn-small" onClick={() => setAsk(null)}>
              Leave it
            </button>
            <button data-action="review.apply" type="button" className="btn btn-primary btn-small" disabled={!!busy} onClick={() => run("apply", () => fix(true))}>
              Change it anyway
            </button>
          </div>
        </div>
      )}
      {ask?.kind === "delete" && (
        <div className="rv-ask" role="alertdialog" aria-label="Delete this note?">
          <p>Delete this note? Its fix, if made, stays.</p>
          <div className="rv-compose-acts">
            <button type="button" className="btn btn-quiet btn-small" onClick={() => setAsk(null)}>
              Keep it
            </button>
            <button
              data-action="review.edit-note"
              type="button"
              className="btn btn-danger btn-small"
              onClick={() =>
                run("delete", async () => {
                  await notesApi.remove(n.id);
                  onDelete(n.id);
                })
              }
            >
              Delete
            </button>
          </div>
        </div>
      )}

      {/* preview + before / after (T3, free) */}
      {a.preview && (
        <div className="rv-preview">
          {!preview || preview.status === "failed" || preview.status === "cancelled" ? (
            <>
              <button data-action="review.compare" type="button" className="btn btn-quiet btn-small" disabled={busy === "preview"} onClick={startPreview}>
                <Icon name="film" size={14} /> Preview the fix
              </button>
              <span className="muted">Free. Just this moment, in about a minute.</span>
              {preview?.status === "failed" && <p className="rv-blocked">{preview.error ?? "The preview didn't work."}</p>}
            </>
          ) : preview.status !== "done" ? (
            <div className="rv-progress" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(preview.progress * 100)} aria-label="Making the preview">
              <span style={{ width: `${Math.max(6, preview.progress * 100)}%` }} />
              <em>Making the preview… {Math.round(preview.progress * 100)}%</em>
            </div>
          ) : ba ? (
            <div className="rv-ab" role="group" aria-label="Before and after">
              <button data-action="review.compare" type="button" className="btn btn-quiet btn-small" onClick={() => player.compare(preview, "before")}>
                Before
              </button>
              <button data-action="review.compare" type="button" className="btn btn-quiet btn-small" onClick={() => player.compare(preview, "after")}>
                After
              </button>
              <button data-action="review.compare" type="button" className="btn btn-primary btn-small" onClick={() => player.compare(preview, "both")}>
                <Icon name="compare" size={14} /> Play both
              </button>
              <button data-action="review.compare" type="button" className="icon-btn" aria-label="Make the preview again" title="Make the preview again" onClick={startPreview}>
                <Icon name="redo" size={16} />
              </button>
            </div>
          ) : null}
        </div>
      )}

      {/* the thread */}
      {(n.thread.length > 1 || n.blockedReason) && (
        <ol className="rv-thread" aria-label="Conversation on this note">
          {n.thread.map((m, i) => (
            <li key={i} className={`rv-msg by-${m.by}`}>
              <b>{m.by === "user" ? "You" : m.by === "director" ? "Director" : "Studio"}</b> {m.text}
            </li>
          ))}
        </ol>
      )}
      {!closed && (n.blockedReason || n.thread.length > 1) && (
        <form
          className="rv-reply"
          onSubmit={(e) => {
            e.preventDefault();
            if (reply.trim()) patch("reply", { reply: { by: "user", text: reply.trim() } }, true, () => setReply(""));
          }}
        >
          <input value={reply} onChange={(e) => setReply(e.target.value)} placeholder={n.blockedReason ? "Answer" : "Reply"} aria-label="Reply on this note" />
          <button type="submit" className="icon-btn" aria-label="Send" disabled={!reply.trim() || busy === "reply"}>
            <Icon name="send" size={16} />
          </button>
        </form>
      )}

      {/* actions */}
      <div className="rv-acts">
        {a.plan && (
          <button data-action="review.plan" type="button" className={`btn btn-small ${n.plan ? "btn-quiet" : "btn-primary"}`} disabled={!!busy} onClick={plan}>
            {busy === "plan" ? "Thinking…" : n.kind === "question" ? "Answer it" : n.plan ? "Plan again" : "Plan the fix"}
          </button>
        )}
        {a.approve && ask?.kind !== "yes" && (
          <button data-action="review.apply" type="button" className="btn btn-primary btn-small" disabled={!!busy} onClick={() => approve(false)}>
            {busy === "approve" ? "Working…" : approveLabel(price)}
          </button>
        )}
        {a.apply && (
          <button data-action="review.apply" type="button" className="btn btn-primary btn-small" disabled={!!busy} onClick={() => run("apply", () => fix(false))}>
            Make the fix
          </button>
        )}
        {a.skip && (
          <button data-action="review.skip" type="button" className="btn btn-quiet btn-small" disabled={!!busy} onClick={() => patch("skip", { status: "skipped" })}>
            Skip
          </button>
        )}
        {a.reopen && (
          <button data-action="review.skip" type="button" className="btn btn-quiet btn-small" disabled={!!busy} onClick={() => patch("reopen", { status: "open" })}>
            Reopen
          </button>
        )}
      </div>
    </div>
  );
}

/** Scrolls the notes panel (desktop: it scrolls on its own) to show `el`, never the page. */
function intoPanel(el: HTMLElement | null, smooth: boolean) {
  const box = el?.closest<HTMLElement>(".rv-side");
  if (!el || !box || box.scrollHeight <= box.clientHeight + 1) return;
  const r = el.getBoundingClientRect();
  const b = box.getBoundingClientRect();
  if (r.top >= b.top && r.bottom <= b.bottom) return;
  const by = r.top < b.top || r.height > b.height ? r.top - b.top - 12 : r.bottom - b.bottom + 12;
  box.scrollBy?.({ top: by, behavior: smooth ? "smooth" : "auto" });
}

/** A step's label without its trailing price ("… (free)", "… (6 credits)"): the price has its own column. */
const stepText = (label: string) => label.replace(/ \((free|\d+ credits?)\)$/, "");
